import React, {
  ForwardedRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { OverlayTrigger } from "react-bootstrap";

import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { ListItemNode, ListNode } from "@lexical/list";
import { ClearEditorPlugin } from "@lexical/react/LexicalClearEditorPlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { $getSelection, CLEAR_EDITOR_COMMAND, LexicalEditor } from "lexical";
import { twMerge } from "tailwind-merge";

import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import { useGlobalState } from "hooks/useGlobalState";

import { AutoLinkNode, LinkNode } from "./nodes/LinkNode";
import { MentionNode } from "./nodes/MentionNode";
import AutoLinkPlugin from "./plugins/AutoLinkPlugin";
import FloatingLinkEditorPlugin from "./plugins/FloatingLinkEditorPlugin";
import FloatingTextFormatToolbarPlugin from "./plugins/FloatingTextFormatToolbarPlugin";
import InitialValuePlugin from "./plugins/InitialValuePlugin";
import LinkPlugin from "./plugins/LinkPlugin";
import MentionsPlugin from "./plugins/MentionsPlugin";
import OnChangePlugin from "./plugins/OnChangePlugin";
import OnKeyDownPlugin from "./plugins/OnKeyDownPlugin";
import RefPlugin from "./plugins/RefPlugin";
import defaultTheme from "./themes/defaultTheme";
import styles from "./styles.module.scss";
import { useValidation } from "./useValidation";

type ContentEditorProps = {
  className?: string;
  editorStyle?:
    | "default"
    | "plain"
    | "comment"
    | "note"
    | "media"
    | "bulletExecutiveSummary"
    | "emailHeadline"
    | "emailBody";
  value: string;
  onInput?: (_value: string) => void;
  placeholder?: string;
  showCharCount?: boolean;
  charMin?: number;
  autoFocus?: boolean;
  charMax?: number;
  onKeyDown?: any;
  inlineToolbar?: boolean;
};

type Ref = { focus?: () => void };

const ContentEditor = (
  {
    className,
    editorStyle,
    value: inputValue,
    onInput,
    placeholder = "To tag, please use: '@' for an Analyst, '$' for Entity and '#' for an Insight",
    showCharCount,
    charMin,
    charMax,
    onKeyDown,
    inlineToolbar = true,
    autoFocus = true,
  }: ContentEditorProps,
  ref: ForwardedRef<Ref>
) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [value, setValue] = useState(inputValue); // for plain style

  const [editor, setEditor] = useState<LexicalEditor>();
  const [floatingAnchorElem, setFloatingAnchorElem] =
    useState<HTMLDivElement | null>(null);

  useImperativeHandle(ref, () => ({
    editor,
    value,
    focus() {
      textAreaRef.current?.focus();
      editor?.focus();
    },
    blur() {
      textAreaRef.current?.blur();
      editor?.blur();
    },
    append(text: string) {
      if (editor) {
        editor.focus();
        editor.update(() => {
          const selection = $getSelection();
          if (selection) {
            selection.insertText(text);
          }
        });
      }
    },
    scrollIntoView(arg?: boolean | ScrollIntoViewOptions) {
      textAreaRef.current?.scrollIntoView(arg);
      editor?.getRootElement()?.scrollIntoView(arg);
    },
  }));

  const [_, setFocusingEditor] = useGlobalState("focusingEditor", false);

  const { isValid, isInvalid, validationTooltip } = useValidation({
    value,
    charMax,
    charMin,
  });

  const editorConfig = useMemo(() => {
    return {
      namespace: "SKEditor",
      // The editor theme
      theme: defaultTheme,
      // Handling of errors during update
      onError(error: any) {
        throw error;
      },
      // Any custom nodes go here
      nodes: [
        HeadingNode,
        ListNode,
        ListItemNode,
        QuoteNode,
        CodeNode,
        CodeHighlightNode,
        TableNode,
        TableCellNode,
        TableRowNode,
        MentionNode,
        AutoLinkNode,
        LinkNode,
      ],
    };
  }, []);

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  useEffect(() => {
    if (inputValue === "") {
      editor?.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
    }
  }, [editor, inputValue]);

  if (editorStyle === "plain") {
    return (
      <div
        className={twMerge(styles.editor, "relative", className)}
        onClick={() => {
          textAreaRef.current!.focus();
        }}
      >
        <textarea
          className="min-h-[180px] w-full appearance-none resize-none outline-none focus:appearance-none focus:resize-none focus:outline-none"
          placeholder={placeholder}
          ref={textAreaRef}
          onChange={(e) => {
            onInput?.(e.currentTarget.value);
            setValue(e.currentTarget.value);
          }}
        />
        {showCharCount && (
          <div
            className={twMerge(
              "absolute right-[3px] bottom-[10px]",
              "text-[13px]",
              !value
                ? "text-black/20"
                : isValid
                ? "text-bullish-green"
                : isInvalid
                ? "text-bearish-red"
                : "text-black/20"
            )}
          >
            {value?.length}
            {validationTooltip && (
              <OverlayTrigger placement="right" overlay={validationTooltip}>
                <span>
                  <FontAwesomeIcon icon={isValid ? faCheck : faInfoCircle} />
                </span>
              </OverlayTrigger>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className={twMerge(styles.editor, className)}>
        <LexicalComposer initialConfig={editorConfig}>
          <div className="editor-container">
            <div className="editor-inner">
              <RefPlugin
                onRef={(editor) => setEditor(editor)}
                onFocus={() => setFocusingEditor(true)}
                onBlur={() => setFocusingEditor(false)}
              />
              <RichTextPlugin
                contentEditable={
                  <div ref={onRef}>
                    <ContentEditable className="editor-input" />
                  </div>
                }
                placeholder={
                  <div
                    className={twMerge(
                      "absolute text-[#ccc] top-[15px] left-[10px]",
                      "font-roboto text-[13px] leading-[1.3]",
                      "pointer-events-none"
                    )}
                  >
                    {placeholder}
                  </div>
                }
                ErrorBoundary={LexicalErrorBoundary}
              />
              <HistoryPlugin />
              <ListPlugin />
              <LinkPlugin />
              <AutoLinkPlugin />
              <MentionsPlugin />
              <InitialValuePlugin htmlString={inputValue} />
              <ClearEditorPlugin />
              <OnChangePlugin
                onChange={(htmlString) => {
                  onInput?.(htmlString);
                  setValue(htmlString);
                }}
              />
              {onKeyDown && <OnKeyDownPlugin onKeyDown={onKeyDown} />}
              {inlineToolbar && floatingAnchorElem && (
                <>
                  <FloatingLinkEditorPlugin anchorElem={floatingAnchorElem} />
                  <FloatingTextFormatToolbarPlugin
                    anchorElem={floatingAnchorElem}
                  />
                </>
              )}
            </div>
          </div>
        </LexicalComposer>
      </div>
    </>
  );
};

export default forwardRef<Ref, ContentEditorProps>(ContentEditor);
